import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { clearAuth, getAuthToken, getRefreshToken, setAuthTokens } from '../Utils';

export const API_BASE_URL = process.env.REACT_APP_API_URL ?? '';

axios.defaults.withCredentials = true;

axios.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token && !config.headers?.has('Authorization')) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

let refreshPromise: Promise<string> | null = null;

const refreshTokens = async (): Promise<string> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('no_refresh_token');
  }
  // Use a bare axios instance so we don't recurse through our own interceptors.
  const bare = axios.create();
  const response = await bare.post('/api/auth/refresh', null, {
    headers: { Authorization: `Bearer ${refreshToken}` },
    withCredentials: true,
  });
  const newAccess = response.headers['accesstoken'];
  const newRefresh = response.headers['refreshtoken'];
  if (!newAccess) {
    throw new Error('refresh_missing_access_token');
  }
  setAuthTokens(newAccess, newRefresh ?? null);
  return newAccess;
};

axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (!originalRequest || originalRequest._retry || originalRequest.url?.includes('/api/auth/')) {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const noResponse = !error.response;
    const headers: any = originalRequest.headers;
    const hadAuth = typeof headers?.has === 'function'
      ? headers.has('Authorization')
      : !!headers?.['Authorization'];
    const hasRefresh = !!getRefreshToken();

    const shouldRefresh = hasRefresh && hadAuth && (status === 401 || noResponse);
    if (!shouldRefresh) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    try {
      if (!refreshPromise) {
        refreshPromise = refreshTokens().finally(() => { refreshPromise = null; });
      }
      const newAccess = await refreshPromise;
      if (typeof originalRequest.headers?.set === 'function') {
        originalRequest.headers.set('Authorization', `Bearer ${newAccess}`);
      } else {
        (originalRequest.headers as any) = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${newAccess}`,
        };
      }
      return axios(originalRequest);
    } catch (refreshError) {
      clearAuth();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    }
  }
);

export default axios;